/**
 * Comprehensive Input Sanitization Utility
 * Provides field-specific filtering, validation, and purification
 */

// Dangerous patterns to detect and remove
const DANGEROUS_PATTERNS = [
    // Script injection patterns
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,

    // SQL injection patterns
    /(\b(union|select|insert|update|delete|drop|create|alter|exec|execute)\b)/gi,
    /(--|\/\*|\*\/|;)/g,

    // XSS patterns
    /<iframe\b[^>]*>/gi,
    /<object\b[^>]*>/gi,
    /<embed\b[^>]*>/gi,
    /<link\b[^>]*>/gi,
    /<meta\b[^>]*>/gi,
    /<style\b[^>]*>.*?<\/style>/gi,

    // Command injection patterns
    /(\||&|;|`|\$\(|\$\{)/g,

    // Path traversal patterns
    /\.\.\//g,
    /\.\.\\/g,

    // Null bytes and control characters
    /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g
];

// Field-specific validation patterns
const FIELD_PATTERNS = {
    firstName: /^[a-zA-Z\s\-'\.]{1,50}$/,
    lastName: /^[a-zA-Z\s\-'\.]{1,50}$/,
    email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    username: /^[a-zA-Z0-9._-]{3,20}$/,
    facebookUrl: /^https?:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9._-]+$/,
    mobileNumber: /^[0-9]{10}$/,
    zipCode: /^[0-9]{5}$/,
    birthday: /^\d{4}-\d{2}-\d{2}$/,
    password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
    confirmPassword: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
    city: /^[a-zA-Z\s\-'\.]{1,50}$/,
    state: /^[a-zA-Z\s\-'\.]{1,50}$/,
    country: /^(US|CA|MX)$/,
    relationshipStatus: /^(single|not_single)$/,
    gender: /^(female|male)$/
};

// Allowed characters for different field types
const ALLOWED_CHARS = {
    name: /[^a-zA-Z\s\-'\.]/g,
    email: /[^a-zA-Z0-9._%+-@]/g,
    username: /[^a-zA-Z0-9._-]/g,
    url: /[^a-zA-Z0-9._:\/\.-]/g,
    numeric: /[^0-9]/g,
    alphanumeric: /[^a-zA-Z0-9]/g,
    date: /[^0-9\-]/g,
    relationship: /[^a-zA-Z_]/g
};

/**
 * Remove dangerous patterns from input
 */
export const removeDangerousPatterns = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input;

    DANGEROUS_PATTERNS.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '');
    });

    return sanitized;
};

/**
 * Escape HTML special characters
 */
export const escapeHtml = (input) => {
    if (typeof input !== 'string') return '';

    const htmlEscapes = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;',
        '`': '&#x60;',
        '=': '&#x3D;'
    };

    return input.replace(/[&<>"'`=\/]/g, (match) => htmlEscapes[match] || match);
};

/**
 * Sanitize text input (names, general text)
 */
export const sanitizeText = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Escape HTML
    sanitized = escapeHtml(sanitized);

    // Remove extra whitespace
    sanitized = sanitized.replace(/\s+/g, ' ');

    return sanitized;
};

/**
 * Sanitize name fields (first name, last name)
 */
export const sanitizeName = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Allow only letters, spaces, hyphens, apostrophes, and periods
    sanitized = sanitized.replace(ALLOWED_CHARS.name, '');

    // Escape HTML
    sanitized = escapeHtml(sanitized);

    // Remove extra whitespace
    sanitized = sanitized.replace(/\s+/g, ' ');

    // Capitalize first letter of each word
    sanitized = sanitized.replace(/\b\w/g, (match) => match.toUpperCase());

    return sanitized;
};

/**
 * Sanitize email input
 */
export const sanitizeEmail = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim().toLowerCase();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Remove characters not allowed in emails
    sanitized = sanitized.replace(ALLOWED_CHARS.email, '');

    // Escape HTML
    sanitized = escapeHtml(sanitized);

    // Remove extra whitespace
    sanitized = sanitized.replace(/\s+/g, '');

    return sanitized;
};

/**
 * Sanitize username input
 */
export const sanitizeUsername = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim().toLowerCase();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Remove characters not allowed in usernames
    sanitized = sanitized.replace(ALLOWED_CHARS.username, '');

    // Escape HTML
    sanitized = escapeHtml(sanitized);

    // Remove extra whitespace
    sanitized = sanitized.replace(/\s+/g, '');

    return sanitized;
};

/**
 * Sanitize URL input (Facebook URL)
 * Note: URLs are not HTML-escaped to preserve their format for validation and display
 */
export const sanitizeUrl = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim().toLowerCase();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Remove characters not allowed in URLs
    sanitized = sanitized.replace(ALLOWED_CHARS.url, '');

    // DO NOT escape HTML for URLs - they need to remain in original format
    // sanitized = escapeHtml(sanitized);  // Removed this line

    // Remove extra whitespace
    sanitized = sanitized.replace(/\s+/g, '');

    return sanitized;
};

/**
 * Sanitize numeric input (mobile number, zip code)
 */
export const sanitizeNumeric = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Keep only digits
    sanitized = sanitized.replace(ALLOWED_CHARS.numeric, '');

    // Escape HTML
    sanitized = escapeHtml(sanitized);

    return sanitized;
};

/**
 * Sanitize date input
 */
export const sanitizeDate = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Keep only digits and hyphens
    sanitized = sanitized.replace(ALLOWED_CHARS.date, '');

    // Escape HTML
    sanitized = escapeHtml(sanitized);

    return sanitized;
};

/**
 * Sanitize password input
 */
export const sanitizePassword = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Keep only alphanumeric and special characters for passwords
    sanitized = sanitized.replace(/[^A-Za-z0-9@$!%*?&]/g, '');

    // DO NOT escape HTML for passwords - they need to remain in original format
    // sanitized = escapeHtml(sanitized);  // Removed this line

    return sanitized;
};

/**
 * Sanitize country code input (only US, CA, MX allowed)
 */
export const sanitizeCountry = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim().toUpperCase();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Only allow US, CA, MX
    const validCountries = ['US', 'CA', 'MX'];
    if (validCountries.includes(sanitized)) {
        return sanitized;
    }

    // If not a valid country, return empty string
    return '';
};

/**
 * Sanitize relationship status/type
 */
export const sanitizeRelationship = (input) => {
    if (typeof input !== 'string') return '';

    let sanitized = input.trim().toLowerCase();

    // Remove dangerous patterns
    sanitized = removeDangerousPatterns(sanitized);

    // Keep only letters and underscores
    sanitized = sanitized.replace(ALLOWED_CHARS.relationship, '');

    // Escape HTML
    sanitized = escapeHtml(sanitized);

    return sanitized;
};

/**
 * Validate field against expected pattern
 */
export const validateField = (fieldName, value) => {
    const pattern = FIELD_PATTERNS[fieldName];
    if (!pattern) return { isValid: true, message: '' };

    const isValid = pattern.test(value);
    let message = '';

    if (!isValid) {
        switch (fieldName) {
            case 'firstName':
            case 'lastName':
                message = 'Name can only contain letters, spaces, hyphens, apostrophes, and periods';
                break;
            case 'email':
                message = 'Please enter a valid email address';
                break;
            case 'username':
                message = 'Username must be 3-20 characters and contain only letters, numbers, dots, underscores, and hyphens';
                break;
            case 'password':
                message = 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
                break;
            case 'confirmPassword':
                message = 'Password confirmation must match the password';
                break;
            case 'facebookUrl':
                message = 'Please enter a valid Facebook profile URL';
                break;
            case 'mobileNumber':
                message = 'Mobile number must be exactly 10 digits';
                break;
            case 'zipCode':
                message = 'Zip code must be exactly 5 digits';
                break;
            case 'city':
                message = 'City can only contain letters, spaces, hyphens, apostrophes, and periods';
                break;
            case 'state':
                message = 'State can only contain letters, spaces, hyphens, apostrophes, and periods';
                break;
            case 'country':
                message = 'Please select a valid country (US, CA, or MX)';
                break;
            case 'birthday':
                message = 'Please enter a valid date';
                break;
            case 'relationshipStatus':
                message = 'Please select a valid relationship status';
                break;
            case 'gender':
                message = 'Please select a valid gender';
                break;
            default:
                message = 'Invalid input format';
        }
    }

    return { isValid, message };
};

/**
 * Sanitize form data object
 */
export const sanitizeFormData = (formData) => {
    const sanitized = {};

    // Sanitize each field based on its type
    Object.keys(formData).forEach(key => {
        const value = formData[key];

        switch (key) {
            case 'firstName':
            case 'lastName':
                sanitized[key] = sanitizeName(value);
                break;
            case 'email':
                sanitized[key] = sanitizeEmail(value);
                break;
            case 'username':
                sanitized[key] = sanitizeUsername(value);
                break;
            case 'password':
            case 'confirmPassword':
                sanitized[key] = sanitizePassword(value);
                break;
            case 'facebookUrl':
                sanitized[key] = sanitizeUrl(value);
                break;
            case 'mobileNumber':
            case 'zipCode':
                sanitized[key] = sanitizeNumeric(value);
                break;
            case 'city':
            case 'state':
                sanitized[key] = sanitizeName(value);
                break;
            case 'country':
                sanitized[key] = sanitizeCountry(value);
                break;
            case 'birthday':
                sanitized[key] = sanitizeDate(value);
                break;
            case 'relationshipStatus':
            case 'gender':
                sanitized[key] = sanitizeRelationship(value);
                break;
            default:
                sanitized[key] = sanitizeText(value);
        }
    });

    return sanitized;
};

/**
 * Validate all form fields
 */
export const validateFormData = (formData) => {
    const errors = {};

    Object.keys(formData).forEach(key => {
        const value = formData[key];

        // Check if field is required and empty
        if (!value || value.trim() === '') {
            switch (key) {
                case 'firstName':
                    errors.firstName = 'First name is required';
                    break;
                case 'lastName':
                    errors.lastName = 'Last name is required';
                    break;
                case 'email':
                    errors.email = 'Email is required';
                    break;
                case 'username':
                    errors.username = 'Username is required';
                    break;
                case 'password':
                    errors.password = 'Password is required';
                    break;
                case 'confirmPassword':
                    errors.confirmPassword = 'Password confirmation is required';
                    break;
                case 'facebookUrl':
                    errors.facebookUrl = 'Facebook URL is required';
                    break;
                case 'relationshipStatus':
                    errors.relationshipStatus = 'Relationship status is required';
                    break;
                case 'gender':
                    errors.gender = 'Gender is required';
                    break;
                case 'birthday':
                    errors.birthday = 'Birthday is required';
                    break;
                case 'mobileNumber':
                    errors.mobileNumber = 'Mobile number is required';
                    break;
                case 'zipCode':
                    errors.zipCode = 'Zip code is required';
                    break;
                case 'city':
                    errors.city = 'City is required';
                    break;
                case 'state':
                    errors.state = 'State is required';
                    break;
                case 'country':
                    errors.country = 'Country is required';
                    break;
            }
            return;
        }

        // Validate field format
        const validation = validateField(key, value);
        if (!validation.isValid) {
            errors[key] = validation.message;
        }
    });

    // Special validation for password confirmation
    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
};

/**
 * Complete form processing: sanitize and validate with comprehensive error handling
 */
export const processFormData = (formData) => {
    const processingErrors = [];
    const sanitizedData = {};
    const validationErrors = {};

    // Process each field individually with error handling
    Object.keys(formData).forEach(fieldName => {
        const value = formData[fieldName];

        try {
            // Sanitize the field
            let sanitizedValue = value;

            switch (fieldName) {
                case 'firstName':
                case 'lastName':
                    sanitizedValue = sanitizeName(value);
                    break;
                case 'email':
                    sanitizedValue = sanitizeEmail(value);
                    break;
                case 'username':
                    sanitizedValue = sanitizeUsername(value);
                    break;
                case 'password':
                case 'confirmPassword':
                    sanitizedValue = sanitizePassword(value);
                    break;
                case 'facebookUrl':
                    sanitizedValue = sanitizeUrl(value);
                    break;
                case 'mobileNumber':
                case 'zipCode':
                    sanitizedValue = sanitizeNumeric(value);
                    break;
                case 'city':
                case 'state':
                    sanitizedValue = sanitizeName(value);
                    break;
                case 'country':
                    sanitizedValue = sanitizeCountry(value);
                    break;
                case 'birthday':
                    sanitizedValue = sanitizeDate(value);
                    break;
                case 'relationshipStatus':
                case 'gender':
                    sanitizedValue = sanitizeRelationship(value);
                    break;
                default:
                    sanitizedValue = sanitizeText(value);
            }

            sanitizedData[fieldName] = sanitizedValue;

            // Validate the sanitized field
            if (!sanitizedValue || sanitizedValue.trim() === '') {
                // Check if field is required
                const requiredFields = ['firstName', 'lastName', 'email', 'username', 'password', 'confirmPassword', 'facebookUrl', 'relationshipStatus', 'gender', 'birthday', 'mobileNumber', 'zipCode', 'city', 'state', 'country'];
                if (requiredFields.includes(fieldName)) {
                    const fieldDisplayName = getFieldDisplayName(fieldName);
                    validationErrors[fieldName] = `${fieldDisplayName} is required`;
                }
            } else {
                // Validate field format
                const validation = validateField(fieldName, sanitizedValue);
                if (!validation.isValid) {
                    validationErrors[fieldName] = validation.message;
                }
            }

        } catch (error) {
            console.error(`Error processing field ${fieldName}:`, error);
            const fieldDisplayName = getFieldDisplayName(fieldName);

            // Create detailed error information
            const fieldError = {
                field: fieldName,
                fieldDisplayName,
                originalValue: value,
                errorType: error.name || 'ProcessingError',
                errorMessage: error.message || 'Unknown error occurred',
                possibleSolutions: getPossibleSolutions(fieldName, error)
            };

            processingErrors.push(fieldError);

            // Set a user-friendly error message
            validationErrors[fieldName] = `${fieldDisplayName} processing failed: ${error.message}`;

            // Use original value if sanitization fails
            sanitizedData[fieldName] = value || '';
        }
    });

    // Create comprehensive error summary if there are processing errors
    const consolidatedErrors = createConsolidatedErrorReport(processingErrors);

    return {
        sanitizedData,
        validationErrors,
        processingErrors,
        consolidatedErrors,
        isValid: Object.keys(validationErrors).length === 0 && processingErrors.length === 0
    };
};

/**
 * Get user-friendly field display name
 */
const getFieldDisplayName = (fieldName) => {
    const displayNames = {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email Address',
        username: 'Username',
        password: 'Password',
        confirmPassword: 'Password Confirmation',
        facebookUrl: 'Facebook Profile URL',
        relationshipStatus: 'Relationship Status',
        gender: 'Gender',
        birthday: 'Birthday',
        mobileNumber: 'Mobile Number',
        zipCode: 'Zip Code',
        city: 'City',
        state: 'State',
        country: 'Country'
    };

    return displayNames[fieldName] || fieldName;
};

/**
 * Get possible solutions for field processing errors
 */
const getPossibleSolutions = (fieldName, error) => {
    const solutions = {
        firstName: [
            'Remove any special characters except spaces, hyphens, apostrophes, and periods',
            'Ensure the name contains only letters',
            'Check for any hidden or invisible characters',
            'Try typing the name again without copy-pasting'
        ],
        lastName: [
            'Remove any special characters except spaces, hyphens, apostrophes, and periods',
            'Ensure the name contains only letters',
            'Check for any hidden or invisible characters',
            'Try typing the name again without copy-pasting'
        ],
        email: [
            'Ensure the email contains only letters, numbers, dots, underscores, percent signs, plus signs, hyphens, and @ symbol',
            'Check for any special characters that might be invalid',
            'Verify the email format is correct (user@domain.com)',
            'Try typing the email again without copy-pasting'
        ],
        username: [
            'Username must be 3-20 characters long',
            'Use only letters, numbers, dots, underscores, and hyphens',
            'No spaces or special characters allowed',
            'Try typing the username again without copy-pasting'
        ],
        password: [
            'Password must be at least 8 characters long',
            'Include at least one uppercase letter (A-Z)',
            'Include at least one lowercase letter (a-z)',
            'Include at least one number (0-9)',
            'Include at least one special character (@$!%*?&)',
            'Remove any spaces or invalid characters'
        ],
        confirmPassword: [
            'Ensure the password confirmation matches the password exactly',
            'Check for any extra spaces or characters',
            'Try typing the password again in both fields',
            'Make sure both passwords are identical'
        ],
        facebookUrl: [
            'Ensure the URL starts with http:// or https://',
            'Check that it contains only letters, numbers, dots, underscores, colons, forward slashes, and hyphens',
            'Verify it points to a Facebook profile (facebook.com/username)',
            'Remove any special characters or spaces'
        ],
        mobileNumber: [
            'Enter only numbers (no spaces, dashes, or other characters)',
            'Ensure exactly 10 digits are entered',
            'Remove any country codes or area codes',
            'Check for any hidden characters'
        ],
        zipCode: [
            'Enter only numbers (no letters or special characters)',
            'Ensure exactly 5 digits are entered',
            'Remove any spaces or dashes',
            'Check for any hidden characters'
        ],
        city: [
            'Remove any special characters except spaces, hyphens, apostrophes, and periods',
            'Ensure the city contains only letters',
            'Check for any hidden or invisible characters',
            'Try typing the city name again without copy-pasting'
        ],
        state: [
            'Remove any special characters except spaces, hyphens, apostrophes, and periods',
            'Ensure the state contains only letters',
            'Check for any hidden or invisible characters',
            'Try typing the state name again without copy-pasting'
        ],
        country: [
            'Please select one of the available countries: United States, Canada, or Mexico',
            'Only US, CA, and MX are valid options',
            'Make sure to select from the dropdown menu'
        ],
        birthday: [
            'Ensure the date is in YYYY-MM-DD format',
            'Check that only numbers and hyphens are used',
            'Verify the date is valid',
            'Remove any spaces or other characters'
        ],
        relationshipStatus: [
            'Select either "Single" or "Not Single"',
            'Check for any extra characters or spaces',
            'Try selecting the option again'
        ],
        gender: [
            'Select either "Female" or "Male"',
            'Check for any extra characters or spaces',
            'Try selecting the option again'
        ]
    };

    // Add generic solutions based on error type
    const genericSolutions = [];

    if (error.message.includes('Maximum call stack')) {
        genericSolutions.push('The input may be too long - try shortening it');
    }

    if (error.message.includes('Invalid character')) {
        genericSolutions.push('Remove any special characters that are not allowed for this field');
    }

    if (error.message.includes('Range out of order')) {
        genericSolutions.push('There may be invalid characters in your input - try typing the value again');
    }

    return [...(solutions[fieldName] || []), ...genericSolutions];
};

/**
 * Create a consolidated error report for user display
 */
const createConsolidatedErrorReport = (processingErrors) => {
    if (processingErrors.length === 0) {
        return null;
    }

    const errorReport = {
        summary: `${processingErrors.length} field(s) failed to process correctly`,
        details: processingErrors.map(error => ({
            field: error.fieldDisplayName,
            originalValue: error.originalValue ? `"${error.originalValue}"` : '(empty)',
            errorType: error.errorType,
            errorMessage: error.errorMessage,
            possibleSolutions: error.possibleSolutions
        })),
        generalAdvice: [
            'Try typing values manually instead of copy-pasting',
            'Check for any hidden or invisible characters',
            'Ensure your input matches the expected format for each field',
            'If problems persist, try refreshing the page and starting over'
        ]
    };

    return errorReport;
};
